import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { assignmentsApi, type AssignmentWorkOrderGroup } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionButton,
  EmptyState,
  ErrorState,
  FilterBar,
  LoadingState,
  PageHeader,
  PaginationNav,
  Panel,
  SearchField,
} from '../../components/ui/index.js';
import { formatNumber } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { groupBySample, groupByWorkOrder, WorkTable } from './grouped-work.js';

/** Work orders per page (D327). Each carries all of the person's open tasks in it, up to 100. */
const GROUPS_PER_PAGE = 10;

/**
 * My Tasks — the caller's own open work, as Work Order → Sample → My Tests
 * (D327).
 *
 * The person is taken from the SESSION by the backend; there is no way to ask
 * this page for somebody else's tasks.
 *
 * MOST PRESSING FIRST, STILL. The server ranks every open task as before —
 * urgent, then high, then the rest; then the date being worked to; then
 * creation — and places each work order by its most pressing task, each
 * sample inside it by its own, so the most urgent work stays on top while
 * every sample is named once. This page only cuts that order.
 *
 * Kept simple on purpose: one search (sample, test, work order), no filters,
 * every group open.
 */
export function MyTasksPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const tasks = useQuery({
    queryKey: ['my-tasks', { page, search, pageBy: 'workOrder' }],
    queryFn: () =>
      assignmentsApi.myTasks({
        page,
        pageSize: GROUPS_PER_PAGE,
        pageBy: 'workOrder',
        ...(search ? { search } : {}),
      }),
    placeholderData: keepPreviousData,
  });
  const rows = tasks.data?.data ?? [];

  return (
    <>
      <PageHeader
        title={t('myTasks.title')}
        subtitle={t('myTasks.subtitle')}
        breadcrumbs={[{ label: t('nav.myTasks') }]}
      />
      <div className="flex flex-col gap-stack">
        <FilterBar>
          <SearchField
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            label={t('work.searchLabel')}
            placeholder={t('myTasks.searchPlaceholder')}
          />
        </FilterBar>

        {tasks.error ? (
          <ErrorState error={tasks.error} onRetry={() => void tasks.refetch()} />
        ) : tasks.isLoading ? (
          <LoadingState />
        ) : rows.length === 0 ? (
          <Panel>
            <EmptyState title={search ? t('myTasks.noneFound') : t('myTasks.none')} />
          </Panel>
        ) : (
          <div className="flex flex-col gap-stack" role="list" aria-label={t('myTasks.groupsLabel')}>
            {groupByWorkOrder(rows, tasks.data?.meta.workOrders ?? []).map(({ group, rows: groupRows }) => (
              <TaskGroup
                key={group.id ?? 'none'}
                group={group}
                rows={groupRows}
                onAttempt={(id) => navigate(`/attempts/${id}`)}
              />
            ))}
            <PaginationNav
              page={page}
              totalPages={tasks.data?.meta.totalPages ?? 1}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>
    </>
  );
}

function TaskGroup({
  group,
  rows,
  onAttempt,
}: {
  readonly group: AssignmentWorkOrderGroup;
  readonly rows: Parameters<typeof WorkTable>[0]['rows'];
  readonly onAttempt: (attemptId: string) => void;
}) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const locale = currentLocale();
  const name = group.id ? (group.number ?? '') : t('work.withoutWorkOrder');
  const parties = [group.clientName, group.projectName].filter(Boolean).join(' / ');
  const total = Math.max(group.itemCount, rows.length);
  const samples = Math.max(group.sampleCount, groupBySample(rows).length);
  const summary = [
    t('work.groupSamples', { total: formatNumber(samples, { locale }) }),
    t('myTasks.groupItems', { total: formatNumber(total, { locale }) }),
    ...(rows.length < total
      ? [t('work.groupShowing', { shown: formatNumber(rows.length, { locale }), total: formatNumber(total, { locale }) })]
      : []),
  ];

  return (
    <section
      role="listitem"
      className="min-w-0 rounded-large border border-default-200 bg-white"
      data-testid="task-group"
      data-work-order={group.id ?? 'none'}
    >
      <header className="min-w-0 px-3 py-2.5 sm:px-4">
        <h2 className="flex min-w-0 flex-wrap items-baseline gap-x-2 text-body font-bold text-default-900">
          {group.id && can(PERMISSIONS.WORK_ORDERS_VIEW) ? (
            <Link
              to={`/work-orders/${group.id}`}
              dir="ltr"
              className="measure underline-offset-2 hover:text-primary-700 hover:underline"
              data-testid="task-group-link"
            >
              {name}
            </Link>
          ) : (
            <span {...(group.id ? { dir: 'ltr', className: 'measure' } : {})}>{name}</span>
          )}
          {parties ? <span className="min-w-0 truncate font-normal text-default-700">{parties}</span> : null}
        </h2>
        <p className="text-caption text-default-600" data-testid="task-group-summary">
          {summary.join(' · ')}
        </p>
      </header>
      <div className="border-t border-default-200 px-1 pb-1 sm:px-2">
        <WorkTable
          label={name}
          rows={rows}
          showAssignee={false}
          action={(row, accessibleName) =>
            row.openAttemptId ? (
              <ActionButton
                priority="secondary"
                size="sm"
                onPress={() => onAttempt(row.openAttemptId!)}
              >
                {t('myTasks.continueDraft')}
              </ActionButton>
            ) : (
              <ActionButton
                priority="tertiary"
                size="sm"
                onPress={() => navigate(`/work/${row.id}`)}
                aria-label={accessibleName}
              >
                {t('common.open')}
              </ActionButton>
            )
          }
        />
      </div>
    </section>
  );
}
